import { getElementAreaIfMouseEventRelated } from '../../Utils';
import React, { useRef, useState } from 'react';
import { OverlayHighlightHandle } from '../../Components/OverlayHighlight';

/**
 * Finds the rank of the overlay with the smallest area where the mouse is. The assumption is the user wants the smaller
 * area behind the larger one if the mouse is over a spot where there are overlapping overlays.
 */
export const findSmallestRelatedRankOverlay = (
  e: React.MouseEvent<HTMLElement>,
  elements: OverlayHighlightHandle[],
) => {
  let bestSoFar = {
    rank: Number.MIN_SAFE_INTEGER,
    area: Number.MAX_SAFE_INTEGER,
  };

  for (const element of elements) {
    if (!element) {
      continue;
    }
    const elementArea = getElementAreaIfMouseEventRelated(e, element);
    if (elementArea && elementArea < bestSoFar.area) {
      bestSoFar = {
        rank: element.getRank(),
        area: elementArea,
      };
    }
  }

  return bestSoFar.rank === Number.MIN_SAFE_INTEGER ? -1 : bestSoFar.rank;
};

export const useOverlayContainer = () => {
  const [hoveringItem, setHoveringItem] = useState<number>(-1);
  const overlayRefs = useRef<OverlayHighlightHandle[]>([]);

  /** A hover function that looks for the smallest overlay under the current mouse position. */
  const allOverlayAwareOnHover = (event: React.MouseEvent<HTMLElement>, isHovering: boolean) => {
    const rank = findSmallestRelatedRankOverlay(event, overlayRefs.current);
    setHoveringItem(existingItem => {
      if (existingItem === rank && !isHovering) {
        return -1;
      } else if (!isHovering) {
        return -1;
      }
      return rank;
    });
  };

  /** A hover function which take the rank as-is without any special mouse processing like {@link allOverlayAwareOnHover} */
  const onHover = (rank: number, isHovering: boolean) => {
    setHoveringItem(existingItem => {
      if (existingItem === rank && !isHovering) {
        return -1;
      } else if (!isHovering) {
        return existingItem;
      }
      return rank;
    });
  };

  return {
    hoveringItem,
    overlayRefs,
    allOverlayAwareOnHover,
    onHover,
  };
};
